import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import MainLayout from "../components/layouts/MainLayout";
import Payment from "../components/views/Payment";
import ModalError from "../components/ModalError";
import * as kiccApi from "../api/kicc";
import * as sanhaApi from "../api/sanha";
import Spinner from "../components/Spinner";
import { RootState } from "../stores/reducers";
import { useNavigate } from "react-router-dom";

type Props = JSX.IntrinsicAttributes & { children?: React.ReactNode };

const CheckInPaymentContainer = (props: Props) => {
  const { bookingItem, paymentInfo, queryStringParams } = useSelector(
    (state: RootState) => state.bookingList
  );

  const [isOpenModalError, setIsOpenModalError] = useState(false);
  const [isError, setIsError] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState("");
  const [modalErrorSubMessage, setModalErrorSubMessage] = useState("");
  const [totalAmount, setTotalAmount] = useState(bookingItem.roomRate);
  const [isLoading, setIsLoading] = useState(false);

  const navigation = useNavigate();

  const registTransaction = async () => {
    try {
      const { data: registedTransaction } = await kiccApi.registTransaction({
        mallId: process.env.REACT_APP_KICC_MALL_ID,
        payMethodTypeCode: "11",
        currency: "00",
        amount: totalAmount || 1000,
        clientTypeCode: "00",
        returnUrl: process.env.REACT_APP_KICC_CALLBACK_URL,
        deviceTypeCode: "mobile",
        shopOrderNo: bookingItem.rsvnNo,
        orderInfo: {
          goodsName: `[${bookingItem.roomTypeCode}] ${bookingItem.roomTypeDesc}`,
        },
      });
      return registedTransaction;
    } catch (error) {
      throw error;
    }
  };

  const confirmCheckInPayment = async () => {
    try {
      setIsLoading(true);
      //결제 여부 체크
      const confirmCheckInPaymentResponse = await sanhaApi.config();
      if (confirmCheckInPaymentResponse.code !== "0000") {
        setModalErrorMessage("예약 결제 여부 확인에 실패 하였습니다.");
        throw new Error(
          `${confirmCheckInPaymentResponse.code}, ${confirmCheckInPaymentResponse.message}`
        );

        // pg 연동 여부 === "N" 이면 체크인 처리(객실 선택 있으면 객실 선택으로)
      } else if (confirmCheckInPaymentResponse.data.pgPaymentYn === "N") {
        navigation("/room");

        // 체크인 결제 여부 === "N" 이면 체크인 처리(객실 선택 있으면 객실 선택으로)
      } else if (confirmCheckInPaymentResponse.data.depositUseYn === "N") {
        navigation("/room");
      } else {
        const confirmCheckInAmountResponse =
          await sanhaApi.confirmCheckInAmount({
            rsvnNo: bookingItem.rsvnNo,
            rsvnSeqNo: "1",
          });
        if (confirmCheckInAmountResponse.code !== "0000") {
          setModalErrorMessage("예약 결제 금액 확인에 실패 하였습니다.");
          throw new Error(
            `${confirmCheckInAmountResponse.code}, ${confirmCheckInAmountResponse.message}`
          );
        } else if (!confirmCheckInAmountResponse.data.totalAmount) {
          navigation("/room");
        } else {
          setTotalAmount(confirmCheckInAmountResponse.data.totalAmount);
        }
      }
    } catch (error: any) {
      setModalErrorMessage(error.response?.data?.message);
      setModalErrorSubMessage(error.message);
      openModalError();
    } finally {
      setIsLoading(false);
    }
  };

  const openModalError = () => {
    setIsOpenModalError(true);
    setIsError(true);
  };

  const closeModalError = () => {
    setIsOpenModalError(false);
  };

  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo.isPaid &&
      paymentInfo.rsvnNo === bookingItem.rsvnNo
    )
      navigation("/room"); //결제 이력이 있을 경우 바로 객실 선택으로
    else if (
      queryStringParams &&
      queryStringParams.responseCode === "0000" &&
      queryStringParams.shopOrderNo === bookingItem.rsvnNo
    ) {
      navigation("/room");
    } else {
      confirmCheckInPayment(); //결제 금액 체크
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div {...props}>
      <MainLayout
        ContentBody={
          <Payment
            bookingItem={bookingItem}
            registTransactionAPI={registTransaction}
            amount={totalAmount}
            setModalErrorMessage={setModalErrorMessage}
            setModalErrorSubMessage={setModalErrorSubMessage}
            openModalError={openModalError}
            isError={isError}
            errorMessage={modalErrorSubMessage}
          />
        }
      />
      <ModalError
        isOpen={isOpenModalError}
        message={modalErrorMessage}
        subMessage={modalErrorSubMessage}
        onClose={closeModalError}
      />
      <Spinner isLoading={isLoading} />
    </div>
  );
};

export default CheckInPaymentContainer;
